import {
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db, rtdb } from "../../@shared/firebase/config"; // adjust import to your project
import {
  get,
  getDatabase,
  ref,
  onDisconnect,
  set,
  query as QUERY,
  orderByChild,
  equalTo,
  limitToFirst,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  off,
  update,
  onValue,
  remove,
} from "firebase/database";

import { v4 as uuidv4 } from "uuid";
import {
  buildCardCountPayload,
  buildLegacyMatchDataFromV2,
  buildLobbyMeta,
  buildMatchPlayersV2,
  buildMatchSummaryV2,
  buildRecentMovesV2,
  buildRoomFromNodes,
} from "../game/rtdbSchema";

const ROOM_META_ALLOWED_KEYS = [
  "accessCode",
  "amountOfPlayers",
  "botFill",
  "public",
  "ranked",
  "timedTurns",
  "turnTime",
  "mode",
  "wager",
  "eventMeta",
  "teamCount",
  "teamOrder",
  "roomOwner",
  "status",
  "createdAt",
  // Story battles store this at the room root so the gameboard can save
  // a Story Mode result handoff before the room is cleaned up.
  "storyBattleMeta",
];
const OPEN_LOBBY_ROOMS_NODE = "openLobbyRooms";

const pickRoomMetaFields = (room = {}) => {
  const out = {};
  ROOM_META_ALLOWED_KEYS.forEach((key) => {
    if (room[key] !== undefined) {
      out[key] = room[key];
    }
  });
  return out;
};

const readLegacyRoomMetaFallbackRTDB = async (db, roomId) => {
  const entries = await Promise.all(
    ROOM_META_ALLOWED_KEYS.map(async (key) => {
      const snap = await get(ref(db, `rooms/${roomId}/${key}`));
      return [key, snap.val()];
    }),
  );

  return entries.reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const getRoomAccessCodeValue = (room = {}) =>
  room?.lobbyMeta?.accessCode ?? room?.accessCode ?? null;

const getRoomStatusValue = (room = {}) =>
  room?.lobbyMeta?.status ?? room?.status ?? null;

const buildOpenLobbyOwnerProfile = (roomOwnerId, players = {}) => {
  const owner = Object.values(players || {}).find((player) => player?.id === roomOwnerId);
  if (!owner && !roomOwnerId) return null;

  return {
    id: owner?.id || roomOwnerId || null,
    username: owner?.username || "Unknown",
    level: Number(owner?.level || 1),
  };
};

export const isEventRoomForLobbyList = (room = {}) => {
  if (!room || typeof room !== "object") return false;

  const eventMeta = room?.eventMeta;
  return Boolean(
    room?.eventId ||
      eventMeta?.eventId ||
      eventMeta?.autoStart ||
      eventMeta?.manualStartDisabled ||
      eventMeta?.scheduledStartMs ||
      (Array.isArray(eventMeta?.participantIds) && eventMeta.participantIds.length),
  );
};

const buildOpenLobbyRoomEntry = (roomId, room = {}) => {
  const builtRoom =
    buildRoomFromNodes({
      roomId,
      lobbyMeta: room?.lobbyMeta || null,
      players: room?.players || {},
      rootFallback: room,
    }) || null;

  if (!builtRoom) return null;

  const players = builtRoom?.players || {};
  const amountOfPlayers = Number(builtRoom?.amountOfPlayers ?? 0);
  const playerIds = Array.isArray(builtRoom?.playerIds)
    ? builtRoom.playerIds
    : Object.keys(players);
  const playerCount = Object.keys(players).length;
  const roomOwnerProfile = buildOpenLobbyOwnerProfile(builtRoom?.roomOwner, players);

  if (
    builtRoom?.public !== true ||
    getRoomStatusValue(builtRoom) !== "lobby" ||
    isEventRoomForLobbyList(builtRoom) ||
    (amountOfPlayers > 0 && playerCount >= amountOfPlayers)
  ) {
    return null;
  }

  return {
    accessCode: builtRoom.accessCode ?? null,
    amountOfPlayers: builtRoom.amountOfPlayers ?? 0,
    botFill: !!builtRoom.botFill,
    ranked: !!builtRoom.ranked,
    mode: builtRoom.mode || "royale",
    wager: builtRoom.wager || null,
    roomOwner: builtRoom.roomOwner || null,
    roomOwnerProfile,
    createdAt: builtRoom.createdAt || Date.now(),
    playerCount,
    playerIds,
    public: true,
    status: "lobby",
    eventMeta: builtRoom.eventMeta || null,
    teamCount: builtRoom.teamCount ?? null,
    teamOrder: builtRoom.teamOrder ?? null,
  };
};

export const removeOpenLobbyRoomIndexRTDB = async (roomId) => {
  if (!roomId) return;
  try {
    await remove(ref(getDatabase(), `${OPEN_LOBBY_ROOMS_NODE}/${roomId}`));
  } catch (error) {
    console.warn("RTDB open-lobby index removal skipped:", error?.message || error);
  }
};

export const syncOpenLobbyRoomIndexRTDB = async (
  roomId,
  roomData = null,
  { allowSnapshotFallback = true } = {},
) => {
  if (!roomId) return null;

  try {
    let sourceRoom = roomData;
    if (!sourceRoom && allowSnapshotFallback) {
      sourceRoom = await getRoomSnapshotRTDB(roomId);
    }

    const nextEntry = buildOpenLobbyRoomEntry(roomId, sourceRoom || {});
    if (!nextEntry) {
      await removeOpenLobbyRoomIndexRTDB(roomId);
      return null;
    }

    await set(ref(getDatabase(), `${OPEN_LOBBY_ROOMS_NODE}/${roomId}`), nextEntry);
    return nextEntry;
  } catch (error) {
    console.warn("RTDB open-lobby index sync skipped:", error?.message || error);
    return null;
  }
};

const normalizeOpenLobbyRoomEntry = (roomId, room = {}) => {
  const legacyPlayers =
    room?.players && typeof room.players === "object" ? room.players : {};
  const legacyPlayerIds = Array.isArray(room?.playerIds)
    ? room.playerIds
    : Object.keys(legacyPlayers);
  const playerCount =
    Number(room?.playerCount) > 0 ||
    Number(room?.playerCount) === 0
      ? Number(room.playerCount)
      : legacyPlayerIds.length;
  const roomOwnerProfile =
    room?.roomOwnerProfile || buildOpenLobbyOwnerProfile(room?.roomOwner, legacyPlayers);

  return {
    id: roomId,
    accessCode: room?.accessCode ?? null,
    amountOfPlayers: Number(room?.amountOfPlayers ?? 0),
    botFill: !!room?.botFill,
    ranked: !!room?.ranked,
    mode: room?.mode || "royale",
    wager: room?.wager || null,
    roomOwner: room?.roomOwner || null,
    roomOwnerProfile,
    createdAt: Number(room?.createdAt || 0) || Date.now(),
    playerCount,
    playerIds: legacyPlayerIds,
    public: room?.public === true,
    status: room?.status || "lobby",
    eventMeta: room?.eventMeta || null,
    teamCount: room?.teamCount ?? null,
    teamOrder: room?.teamOrder ?? null,
  };
};

const findRoomByAccessCodeFromRoomsNode = (rooms = {}, normalizedCode) => {
  const normalized = String(normalizedCode || "").trim();
  if (!normalized) return null;

  return (
    Object.entries(rooms || {}).find(([, room]) => {
      const accessCode = getRoomAccessCodeValue(room);
      return String(accessCode ?? "").trim() === normalized;
    }) || null
  );
};

const buildLobbyListRoom = (roomId, room = {}) => {
  const built = buildRoomFromNodes({
    roomId,
    lobbyMeta: room?.lobbyMeta || null,
    players: room?.players || {},
    rootFallback: room,
  });

  if (!built) return null;

  return {
    ...built,
    players: Object.values(built.players || {}),
  };
};

const buildVisibleLobbyRoomsFromRoomsNode = (roomsNode = {}, pageSize = 100) =>
  Object.entries(roomsNode || {})
    .map(([roomId, room]) => buildLobbyListRoom(roomId, room))
    .filter(Boolean)
    .filter((room) => {
      const players = Array.isArray(room.players)
        ? room.players
        : Object.values(room.players || {});
      const amountOfPlayers = Number(room.amountOfPlayers ?? 0);
      return (
        room.public === true &&
        !isEventRoomForLobbyList(room) &&
        getRoomStatusValue(room) === "lobby" &&
        (!amountOfPlayers || players.length < amountOfPlayers)
      );
    })
    .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0))
    .slice(0, pageSize);

const fetchVisibleLobbyRoomsFromRTDB = async (pageSize = 100) => {
  const dbRef = getDatabase();
  const roomsRef = ref(dbRef, "rooms");

  try {
    const indexedQuery = QUERY(
      roomsRef,
      orderByChild("status"),
      equalTo("lobby"),
      limitToFirst(pageSize),
    );
    const snapshot = await get(indexedQuery);
    const rooms = buildVisibleLobbyRoomsFromRoomsNode(
      snapshot.val() || {},
      pageSize,
    );
    if (rooms.length > 0) {
      return rooms;
    }
  } catch (rtdbError) {
    console.warn(
      "RTDB indexed lobby list unavailable; using root scan fallback:",
      rtdbError?.message || rtdbError,
    );
  }

  try {
    const snapshot = await get(roomsRef);
    return buildVisibleLobbyRoomsFromRoomsNode(snapshot.val() || {}, pageSize);
  } catch (fallbackError) {
    console.error("Failed to fetch lobby rooms from RTDB:", fallbackError);
    return [];
  }
};

export const writeRoomAccessCodeLookupRTDB = async ({
  accessCode,
  roomId,
  status = "lobby",
  createdAt = Date.now(),
}) => {
  if (accessCode === undefined || accessCode === null || !roomId) return;
  const db = getDatabase();
  try {
    await set(ref(db, `roomAccessCodes/${String(accessCode)}`), {
      roomId,
      status,
      createdAt,
    });
    return true;
  } catch (error) {
    console.warn("RTDB access-code lookup write skipped:", error?.message || error);
    return false;
  }
};

export const removeRoomAccessCodeLookupRTDB = async (accessCode) => {
  if (accessCode === undefined || accessCode === null || accessCode === "") return;
  const db = getDatabase();
  try {
    await remove(ref(db, `roomAccessCodes/${String(accessCode)}`));
    return true;
  } catch (error) {
    console.warn("RTDB access-code lookup removal skipped:", error?.message || error);
    return false;
  }
};

export const resolveRoomIdByAccessCodeRTDB = async (accessCode) => {
  const normalizedCode = String(accessCode || "").trim();
  if (!normalizedCode) {
    throw new Error("Please enter a room code.");
  }

  const db = getDatabase();
  try {
    const lookupSnap = await get(ref(db, `roomAccessCodes/${normalizedCode}`));
    const lookupData = lookupSnap.val();
    if (lookupData?.roomId) {
      return {
        roomId: lookupData.roomId,
        lookupData,
        legacyLookupUsed: false,
      };
    }
  } catch (error) {
    console.warn(
      "RTDB access-code lookup read skipped; falling back to legacy room query:",
      error?.message || error,
    );
  }

  const roomsRef = ref(db, "rooms");
  const queryValues = [normalizedCode];
  const numericCode = Number(normalizedCode);
  if (Number.isFinite(numericCode)) {
    queryValues.unshift(numericCode);
  }

  let indexedLookupError = null;

  for (const candidate of queryValues) {
    try {
      const q = QUERY(
        roomsRef,
        orderByChild("accessCode"),
        equalTo(candidate),
        limitToFirst(1),
      );
      const snapshot = await get(q);
      const rooms = snapshot.val() || {};
      const roomId = Object.keys(rooms)[0];
      if (!roomId) continue;

      const roomData = rooms[roomId] || {};
      await writeRoomAccessCodeLookupRTDB({
        accessCode: roomData?.accessCode ?? normalizedCode,
        roomId,
        status: roomData?.status || "lobby",
        createdAt: roomData?.createdAt || Date.now(),
      });

      return {
        roomId,
        lookupData: roomData,
        legacyLookupUsed: true,
      };
    } catch (error) {
      indexedLookupError = error;
      const message = String(error?.message || error || "");
      if (!message.includes("Index not defined")) {
        throw error;
      }
      break;
    }
  }

  if (indexedLookupError) {
    console.warn(
      "RTDB indexed access-code lookup unavailable; using root scan fallback:",
      indexedLookupError?.message || indexedLookupError,
    );
  }

  const rootSnap = await get(roomsRef);
  const match = findRoomByAccessCodeFromRoomsNode(
    rootSnap.val() || {},
    normalizedCode,
  );

  if (match) {
    const [roomId, roomData] = match;
    await writeRoomAccessCodeLookupRTDB({
      accessCode: getRoomAccessCodeValue(roomData) ?? normalizedCode,
      roomId,
      status: getRoomStatusValue(roomData) || "lobby",
      createdAt: roomData?.lobbyMeta?.createdAt || roomData?.createdAt || Date.now(),
    });

    return {
      roomId,
      lookupData: roomData,
      legacyLookupUsed: true,
      rootScanFallbackUsed: true,
    };
  }

  throw new Error("Room not found.");
};

export const deleteRoomCascadeRTDB = async (roomId, accessCode = null) => {
  if (!roomId) return;
  const db = getDatabase();
  const updates = {
    [`rooms/${roomId}`]: null,
    [`lobbyChats/${roomId}`]: null,
  };
  await update(ref(db), updates);
  await removeOpenLobbyRoomIndexRTDB(roomId);
  if (accessCode !== undefined && accessCode !== null && accessCode !== "") {
    await removeRoomAccessCodeLookupRTDB(accessCode);
  }
};

export const getRoomSnapshotRTDB = async (
  roomId,
  { includeLegacyFallback = true } = {},
) => {
  if (!roomId) return null;

  const db = getDatabase();
  const [lobbyMetaSnap, playersSnap, playerIdsSnap, storyBattleMetaSnap] = await Promise.all([
    get(ref(db, `rooms/${roomId}/lobbyMeta`)),
    get(ref(db, `rooms/${roomId}/players`)),
    get(ref(db, `rooms/${roomId}/playerIds`)),
    get(ref(db, `rooms/${roomId}/storyBattleMeta`)),
  ]);

  let lobbyMeta = lobbyMetaSnap.val() || null;
  const players = playersSnap.val() || {};
  const rootFallback = {};

  if (playerIdsSnap.exists()) {
    rootFallback.playerIds = playerIdsSnap.val();
  }

  if (storyBattleMetaSnap.exists()) {
    rootFallback.storyBattleMeta = storyBattleMetaSnap.val();
  }

  if (!lobbyMeta && includeLegacyFallback) {
    const legacyMeta = await readLegacyRoomMetaFallbackRTDB(db, roomId);
    if (Object.keys(legacyMeta).length) {
      Object.assign(rootFallback, legacyMeta);
      lobbyMeta = buildLobbyMeta(legacyMeta) || legacyMeta;
    }
  }

  return (
    buildRoomFromNodes({
      roomId,
      lobbyMeta,
      players,
      rootFallback,
    }) || null
  );
};

export const getGameplayMatchSnapshotRTDB = async (
  roomId,
  { includeLegacyFallback = true, requirePlayers = false } = {},
) => {
  if (!roomId) return null;

  const db = getDatabase();
  let splitReadError = null;

  try {
    const [summarySnap, playersSnap, recentMovesSnap] = await Promise.all([
      get(ref(db, `rooms/${roomId}/matchData/summary`)),
      get(ref(db, `rooms/${roomId}/matchData/players`)),
      get(ref(db, `rooms/${roomId}/matchData/recentMoves`)),
    ]);

    const summary = summarySnap.val() || null;
    const playersById = playersSnap.val() || {};
    const recentMovesNode = recentMovesSnap.val() || {};

    if (summary && Number(summary.schemaVersion) === 2) {
      const splitMatch = buildLegacyMatchDataFromV2({
        summary,
        playersById,
        recentMovesNode,
        legacyFallback: null,
      });
      const hasPlayers =
        Array.isArray(splitMatch?.players) && splitMatch.players.length > 0;

      if (!requirePlayers || hasPlayers) {
        return splitMatch;
      }
    }
  } catch (error) {
    splitReadError = error;
  }

  if (!includeLegacyFallback) {
    if (splitReadError) {
      throw splitReadError;
    }
    return null;
  }

  try {
    const legacySnap = await get(ref(db, `rooms/${roomId}/matchData/match`));
    return legacySnap.val() || null;
  } catch (legacyError) {
    throw splitReadError || legacyError;
  }
};

export const syncLegacyMatchToSplitNodesRTDB = async (roomId, matchData) => {
  if (!roomId || !matchData) return;

  const summary = buildMatchSummaryV2(matchData);
  const players = buildMatchPlayersV2(matchData?.players || []);
  const recentMoves = buildRecentMovesV2(matchData?.moves || []);

  await update(ref(getDatabase()), {
    [`rooms/${roomId}/matchData/summary`]: summary || {},
    [`rooms/${roomId}/matchData/players`]: players,
    [`rooms/${roomId}/matchData/recentMoves`]: recentMoves,
  });
};

export const createRoom = async (userId, settings) => {
  const accessCode = Math.floor(1000000 + Math.random() * 9000000);

  const roomRef = doc(collection(db, "rooms"));
  const roomId = roomRef.id;

  const roomData = {
    accessCode,
    amountOfPlayers: settings.amountOfPlayers || 2,
    botFill: settings.botFill || false,
    playerIds: [userId],
    players: settings.players || [],
    public: settings.public || false,
    roomOwner: userId,
    status: "lobby",
    timedTurns: settings.timedTurns || false,
    turnTime: settings.turnTime || 30,
    ranked: settings.ranked || false,
    mode: settings.mode || 'royale',
  };

  // 1. Create the room doc
  await setDoc(roomRef, roomData);

  // 2. Add initial message to chatThreadData subcollection
  const initialChatRef = doc(collection(db, "rooms", roomId, "chatThreadData"));
  await setDoc(initialChatRef, {
    sender: "system",
    message: "Welcome to the room!",
    createdAt: new Date(),
  });

  return { id: roomId, ...roomData };
};

export const joinRoom = async (accessCode, userData) => {
  const q = query(
    collection(db, "rooms"),
    where("accessCode", "==", accessCode)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    throw new Error("Room not found");
  }

  const roomDoc = querySnapshot.docs[0];
  const roomData = roomDoc.data();

  if (roomData.playerIds.includes(userData.id)) {
    return { id: roomDoc.id, ...roomData };
  }

  if (roomData.playerIds.length >= roomData.amountOfPlayers) {
    throw new Error("Room is full");
  }

  const updatedPlayerIds = [...roomData.playerIds, userData.id];
  const updatedPlayers = [...roomData.players, userData];

  await updateDoc(doc(db, "rooms", roomDoc.id), {
    playerIds: updatedPlayerIds,
    players: updatedPlayers,
  });

  return {
    id: roomDoc.id,
    ...roomData,
    playerIds: updatedPlayerIds,
    players: updatedPlayers,
  };
};

export const getLobbyRooms = async (startAfterDoc = null, pageSize = 100) => {
  if (startAfterDoc) {
    return { rooms: [], lastDoc: null };
  }

  try {
    const snapshot = await get(ref(getDatabase(), OPEN_LOBBY_ROOMS_NODE));
    const rooms = Object.entries(snapshot.val() || {})
      .map(([roomId, room]) => ({ id: roomId, ...(room || {}) }))
      .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0))
      .slice(0, pageSize);

    if (rooms.length > 0) {
      return { rooms, lastDoc: null };
    }
  } catch (openLobbyError) {
    console.warn(
      "Open lobby index unavailable; falling back to legacy lobby fetch:",
      openLobbyError?.message || openLobbyError,
    );
  }

  const legacyRooms = await fetchVisibleLobbyRoomsFromRTDB(pageSize);
  if (legacyRooms.length > 0) {
    return { rooms: legacyRooms, lastDoc: null };
  }

  try {
    const baseQuery = query(
      collection(db, "rooms"),
      where("status", "==", "lobby"),
      orderBy("accessCode", "desc"), // or any other sortable field like createdAt
      limit(pageSize)
    );

    const paginatedQuery = startAfterDoc
      ? query(baseQuery, startAfter(startAfterDoc))
      : baseQuery;

    // console.log("paginatedQuery", paginatedQuery);

    const snapshot = await getDocs(paginatedQuery);
    console.log("snapshot");

    const rooms = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("rooms from function call", rooms);

    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;

    return { rooms, lastDoc };
  } catch (error) {
    console.error("🔥 Error in getLobbyRooms:", error);
    return { rooms: [], lastDoc: null };
  }
};

export const subscribeToOpenLobbyRooms = (callback, onError = null) => {
  let active = true;

  const emitFallbackRooms = async (error = null) => {
    const fallbackRooms = await fetchVisibleLobbyRoomsFromRTDB();
    if (!active) return;

    callback(fallbackRooms);

    if (
      error &&
      fallbackRooms.length === 0 &&
      typeof onError === "function"
    ) {
      onError(error);
    }
  };

  const unsubscribe = onValue(
    ref(getDatabase(), OPEN_LOBBY_ROOMS_NODE),
    async (snapshot) => {
      const rooms = Object.entries(snapshot.val() || {})
        .map(([roomId, room]) => normalizeOpenLobbyRoomEntry(roomId, room || {}))
        .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));

      if (rooms.length > 0) {
        callback(rooms);
        return;
      }

      await emitFallbackRooms();
    },
    async (error) => {
      console.error("Failed to subscribe to open lobby rooms:", error);
      await emitFallbackRooms(error);
    },
  );

  return () => {
    active = false;
    if (typeof unsubscribe === "function") {
      unsubscribe();
    }
  };
};

// Subscribe to real-time updates on a room
export const subscribeToRoom = (roomId, callback) => {
  const unsub = roomId && onSnapshot(doc(db, "rooms", roomId), (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap?.id, ...docSnap.data() });
    }
  });

  return unsub; // Call this to unsubscribe
};

// NEW: subscribe to RTDB rooms/{roomId}
export const subscribeToRoomRTDB = (roomId, cb) => {
  const db = getDatabase();
  const lobbyMetaRef = ref(db, `rooms/${roomId}/lobbyMeta`);
  const playersRef = ref(db, `rooms/${roomId}/players`);
  const storyBattleMetaRef = ref(db, `rooms/${roomId}/storyBattleMeta`);

  let lobbyMeta = null;
  let players = {};
  let storyBattleMeta = null;
  let rootFallback = null;
  let attemptedLegacyFallback = false;

  const emit = () => {
    const rootRoomData = {
      ...(rootFallback || {}),
      ...(storyBattleMeta ? { storyBattleMeta } : {}),
    };
    const room = buildRoomFromNodes({
      roomId,
      lobbyMeta,
      players,
      rootFallback: Object.keys(rootRoomData).length ? rootRoomData : null,
    });
    cb(room);
  };

  const ensureLegacyFallback = async () => {
    if (attemptedLegacyFallback) return;
    attemptedLegacyFallback = true;

    const legacyMeta = await readLegacyRoomMetaFallbackRTDB(db, roomId);
    if (!Object.keys(legacyMeta).length) {
      cb(null);
      return;
    }

    rootFallback = {
      ...(rootFallback || {}),
      ...legacyMeta,
    };
    if (!lobbyMeta) {
      lobbyMeta = buildLobbyMeta(legacyMeta) || legacyMeta;
    }

    emit();
  };

  const unsubs = [
    onValue(lobbyMetaRef, async (snap) => {
      lobbyMeta = snap.val();
      if (!lobbyMeta) {
        await ensureLegacyFallback();
        return;
      }
      emit();
    }),
    onValue(playersRef, async (snap) => {
      players = snap.val() || {};
      if (!lobbyMeta && !attemptedLegacyFallback) {
        await ensureLegacyFallback();
        return;
      }
      emit();
    }),
    onValue(storyBattleMetaRef, (snap) => {
      storyBattleMeta = snap.val();
      emit();
    }),
  ];

  return () => {
    unsubs.forEach((unsub) => {
      try {
        unsub?.();
      } catch {}
    });
  };
};



export function updatePlayerPresence(
  userData,
  {
    ...Payload
    // partyId = "",
    // host = false,
    // inGameRoom = false,
    // roomId = "",
    // emote = "",
    // x = 0,
    // y = 0,
    // direction = 90,
    // animState = "idle",
    // device = "mobile",
    // building = "",
  }
) {

   if (!userData) return () => {}; // ✅ guard

  const db = getDatabase();
  const presRef = ref(db, `presence/${userData?.id}`);

  const payload = {
    ...Payload,
    lastUpdated: Date.now(),
  };

  //   console.log("Payload", payload);

  // Write presence
  update(presRef, payload);

  // Clean up on disconnect
  onDisconnect(presRef).remove();
}

/**
 * Listen to everyone in the same partyId.
 * callback(type, userId, data?)
 *   type: "added" | "changed" | "removed"
 */
export function subscribeToPartyPresence(partyId, callback) {
  if (!partyId) return () => {}; // ✅ guard
  
  const db = getDatabase();
  const presRef = ref(db, "presence");
  const q = QUERY(presRef, orderByChild("partyId"), equalTo(partyId));

  const addedFn = (snapshot) => {
    const data = snapshot.val();
    // console.log("[subscribeToPartyPresence] 🔵 added:", snapshot.key, data);
    callback("added", snapshot.key, data);
  };

  const changedFn = (snapshot) => {
    const data = snapshot.val();
    // console.log("[subscribeToPartyPresence] 🟡 changed:", snapshot.key, data);
    callback("changed", snapshot.key, data);
  };

  const removedFn = (snapshot) => {
    // console.log("[subscribeToPartyPresence] 🔴 removed:", snapshot.key);
    callback("removed", snapshot.key);
  };

  onChildAdded(q, addedFn);
  onChildChanged(q, changedFn);
  onChildRemoved(q, removedFn);

  return () =>
    off(q, "child_added", addedFn) &
    off(q, "child_changed", changedFn) &
    off(q, "child_removed", removedFn);
}

export const getAvailableHuman = async (players, roomId) => {
  console.log("checking avail humans");
  const rtdb = getDatabase();
  const humans = players.filter((p) => !p.isBot);

  for (let player of humans) {
    const presenceSnap = await get(ref(rtdb, `presence/${player.id}`));
    const presence = presenceSnap.val();

    // console.log("Presence =>", presence, "Player is available", player);

    if (presence?.roomId === roomId && presence?.inGameRoom) {
      return player;
    }
  }

  return null;
};

export const savePlayerDeckAndHand = async (roomId, userId, { deck, hand }) => {
  try {
    const deckRef = doc(db, "rooms", roomId, "decks", userId);
    const handRef = doc(db, "rooms", roomId, "hands", userId);

    const updates = [];

    if (deck !== null) {
      updates.push(setDoc(deckRef, { cards: deck }, { merge: true }));
    }

    if (hand !== null) {
      updates.push(setDoc(handRef, { cards: hand }, { merge: true }));
    }

    await Promise.all(updates);

    // console.log(`🔥 [Deck+Hand] Saved for user ${userId} in room ${roomId}`);
  } catch (err) {
    console.error("🚨 Failed to save deck/hand:", err);
  }
};

export const updateHandAndDeck = async (
  roomId,
  playerId,
  hand,
  deck = null
) => {
  const updates = [
    updateDoc(doc(db, "rooms", roomId, "hands", playerId), { cards: hand }),
  ];

  if (deck) {
    updates.push(
      updateDoc(doc(db, "rooms", roomId, "decks", playerId), { cards: deck })
    );
  }

  return Promise.all(updates);
};

export const createAndSetRoomRTDB = async (userId, roomSettings, newRoomId) => {
  const roomId = newRoomId ? newRoomId : uuidv4(); // or use push() if desired
  const now = Date.now();
  const { skipCreatorAsPlayer = false, ...persistedRoomSettings } =
    roomSettings || {};

  const playerIds = skipCreatorAsPlayer ? [] : [userId];
  const roomMeta = {
    ...pickRoomMetaFields(persistedRoomSettings),
    roomOwner: userId,
    status: "lobby",
    createdAt: now,
  };
  const lobbyMeta = buildLobbyMeta(roomMeta) || roomMeta;
  const roomData = {
    ...persistedRoomSettings,
    ...roomMeta,
    playerIds,
    players: persistedRoomSettings.players || {},
    lobbyMeta,
  };

  const roomRef = ref(rtdb, `rooms/${roomId}`);
  await set(roomRef, roomData);
  await writeRoomAccessCodeLookupRTDB({
    accessCode: lobbyMeta?.accessCode,
    roomId,
    status: lobbyMeta?.status || "lobby",
    createdAt: lobbyMeta?.createdAt || now,
  });
  await syncOpenLobbyRoomIndexRTDB(roomId, roomData, {
    allowSnapshotFallback: false,
  });

  return roomId;
};

export const updateRoomSettingsRTDB = async (roomId, updates) => {
  const db = getDatabase();
  const roomRef = ref(db, `rooms/${roomId}`);

  const allowedKeys = [
    "amountOfPlayers",
    "botFill",
    "public",
    "ranked",
    // "teams",
    "timedTurns",
    "turnTime",
    // "pve",
    "mode",
    "wager",
    "teamCount",
    "teamOrder",
    "eventMeta",
  ];

  const payload = {};
  for (const k of allowedKeys) {
    if (k in updates) payload[k] = updates[k];
  }

  // if timer is off, null out the turnTime (or keep last value if you prefer)
  if (payload.timedTurns === false) payload.turnTime = null;

  const dualPayload = { ...payload };
  Object.entries(payload).forEach(([key, value]) => {
    dualPayload[`lobbyMeta/${key}`] = value;
  });

  await update(roomRef, dualPayload);
  await syncOpenLobbyRoomIndexRTDB(roomId);
};



export const fetchLobbyRooms = async () => {
  const { rooms } = await getLobbyRooms(null, 100);
  return rooms;
};

export const joinRoomRTDB = async (roomId, id, userData) => {
  const db = getDatabase();
  const roomRef = ref(db, `rooms/${roomId}`);

  // 1️⃣ Get room data
  const room = await getRoomSnapshotRTDB(roomId);

  if (!room) throw new Error("Room not found");

  const lobbyMeta = buildLobbyMeta(room) || room?.lobbyMeta || null;
  if (!room?.lobbyMeta && lobbyMeta) {
    await update(ref(db, `rooms/${roomId}`), { lobbyMeta });
  }

  // 2️⃣ Add player
  const playerRef = ref(db, `rooms/${roomId}/players/${id}`);
  const playerData = {
    ...userData,
    id,
  };
  await update(playerRef, playerData);

  // 3️⃣ Update playerIds (avoid duplicates)
  const existingIds = room.playerIds || [];
  const newPlayerIds = existingIds.includes(id)
    ? existingIds
    : [...existingIds, id];

  await update(roomRef, {
    playerIds: newPlayerIds,
  });

  // 4️⃣ Return the updated room data to set in Zustand
  const updatedRoom = buildRoomFromNodes({
    roomId,
    lobbyMeta,
    players: {
      ...(room.players || {}),
      [id]: playerData,
    },
    rootFallback: {
      ...room,
      playerIds: newPlayerIds,
    },
  });
  await syncOpenLobbyRoomIndexRTDB(roomId, updatedRoom, {
    allowSnapshotFallback: false,
  });
  return updatedRoom;
};

export const createMatchRTDB = async (roomId, players) => {
  const db = getDatabase();

  const playerIds = Object.keys(players);
  const shuffledOrder = [...playerIds].sort(() => Math.random() - 0.5);
  const firstPlayerId = shuffledOrder[0];
  const matchPresence = Object.fromEntries(
    playerIds.map((id) => [
      id,
      {
        userId: id,
        ready: !!players?.[id]?.ready,
        present: true,
        updatedAt: Date.now(),
      },
    ])
  );

  const updates = {
    status: "match",
    turnOrder: shuffledOrder,
    currentTurn: firstPlayerId,
    turnCount: 0,
    matchStartedAt: Date.now(),
  };

  // Update room-level status
  await update(ref(db, `rooms/${roomId}`), updates);

  // Initialize matchData node
  await update(ref(db, `rooms/${roomId}/matchData`), {
    turnOrder: shuffledOrder,
    currentTurn: firstPlayerId,
    turnCount: 0,
    effects: {},
    matchPresence,
    playerPoints: Object.fromEntries(
      playerIds.map((id) => [id, { points: 0, eliminated: false }])
    ),
  });

  await update(ref(db, `rooms/${roomId}`), {
    "lobbyMeta/status": "match",
  });
  await removeOpenLobbyRoomIndexRTDB(roomId);
};

export const updateHandAndDeckRTDB = async (
  roomId,
  playerId,
  hand,
  deck = null
) => {
  try {
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const updates = {
      [`hands/${playerId}/cards`]: hand,
      [`matchData/cardCounts/${playerId}/handCount`]: Array.isArray(hand)
        ? hand.length
        : 0,
      [`matchData/cardCounts/${playerId}/updatedAt`]: Date.now(),
    };
    if (deck !== null && deck !== undefined) {
      updates[`decks/${playerId}/cards`] = deck;
      updates[`matchData/cardCounts/${playerId}/deckCount`] = Array.isArray(deck)
        ? deck.length
        : 0;
    }
    await update(roomRef, updates);

    console.log(
      `[RTDB] Updated hand${deck ? " and deck" : ""} for ${playerId}`
    );
  } catch (err) {
    console.error("🚨 [RTDB] Failed to update hand/deck:", err);
  }
};

export const savePlayerDeckAndHandRTDB = async (
  roomId,
  playerId,
  { hand, deck }
) => {
  try {
    const db = getDatabase();
    const rootRef = ref(db, `rooms/${roomId}`);
    const updates = {};
    if (deck !== null && deck !== undefined) {
      updates[`decks/${playerId}/cards`] = deck;
    }
    if (hand !== null && hand !== undefined) {
      updates[`hands/${playerId}/cards`] = hand;
    }
    if (hand !== null && hand !== undefined) {
      updates[`matchData/cardCounts/${playerId}`] = buildCardCountPayload(hand, deck);
    }
    if (Object.keys(updates).length > 0) {
      await update(rootRef, updates);
    }

    // console.log(`[RTDB] Saved deck and hand for player ${playerId} in room ${roomId}`);
  } catch (err) {
    console.error("🚨 [RTDB] Failed to save deck/hand:", err);
  }
};


// ---------------------------------------------
// Presence helpers (ONLINE vs OFFLINE)
// ---------------------------------------------
export function isPresenceNotOffline(presence) {
  if (!presence) return false;

  // New preferred field:
  if (typeof presence.status === "string") {
    return presence.status !== "offline";
  }

  // Backward compatible fallback:
  return !!presence.online;
}

export async function setPlayerOffline(userId) {
  if (!userId) return;
  const db = getDatabase();
  const presRef = ref(db, `presence/${userId}`);

  // Keep the node (optional) but mark offline
  await update(presRef, {
    online: false,
    status: "offline",
    lastUpdated: Date.now(),
  });
}


export async function getPresence(userId) {
  const db = getDatabase();
  const snap = await get(ref(db, `presence/${userId}`));
  return snap.val();
}

export async function areAllHumansPresentInRoom(players = [], roomId) {
  const humans = players.filter((p) => p && !p.isBot);
  if (!humans.length) return true;

  const presences = await Promise.all(humans.map((p) => getPresence(p.id)));

  return presences.every((pr) => pr?.roomId === roomId && pr?.inGameRoom === true);
}

/**
 * Subscribes to the current user's friends list (Firestore) and then listens to
 * each friend's presence (RTDB). Calls onCount with the number of friends whose
 * presence is NOT offline.
 *
 * Returns: unsubscribe() that cleans up Firestore + RTDB listeners.
 */
export function subscribeToFriendsOnlineCount(userId, onCount) {
  if (!userId) return () => {};

  // 1) Firestore friends subcollection listener
  const friendsRef = collection(db, "users", userId, "friends");
  const friendsQuery = query(friendsRef, where("status", "==", "friend"));

  const rtdbDb = getDatabase();

  // RTDB listener bookkeeping
  let presenceUnsubs = [];
  const presenceById = new Map();

  const cleanupPresenceListeners = () => {
    presenceUnsubs.forEach((fn) => {
      try {
        fn?.();
      } catch {}
    });
    presenceUnsubs = [];
    presenceById.clear();
  };

  const computeAndEmit = () => {
    let count = 0;
    for (const p of presenceById.values()) {
      if (isPresenceNotOffline(p)) count += 1;
    }
    onCount?.(count);
  };

  const unsubFirestore = onSnapshot(friendsQuery, (snap) => {
    const friendIds = snap.docs.map((d) => d.id);

    // Rebuild RTDB listeners any time the friend list changes
    cleanupPresenceListeners();

    if (friendIds.length === 0) {
      onCount?.(0);
      return;
    }

    // 2) RTDB presence listeners per friend
    presenceUnsubs = friendIds.map((fid) => {
      const pRef = ref(rtdbDb, `presence/${fid}`);
      const unsub = onValue(pRef, (s) => {
        presenceById.set(fid, s.val() || null);
        computeAndEmit();
      });
      return unsub;
    });

    // Emit immediately (in case none have data yet)
    computeAndEmit();
  });

  return () => {
    try {
      unsubFirestore?.();
    } catch {}
    cleanupPresenceListeners();
  };
}




